import { locale } from '../lib/locale';

/**
 * Tasks (once or regular) and a person's own page (1.19). Stars for kids, points for teens (glossary).
 * German is the master; English has the same keys.
 */
export const TASKS_TEXT = {
  de: {
    stars: 'Sterne',
    points: 'Punkte',
    dayparts: {
      morning: 'Morgens',
      afternoon: 'Nachmittags',
      evening: 'Abends',
      anytime: 'Irgendwann heute',
    },
    now: ' · jetzt',
    whoHelped: 'Wer hat mitgemacht?',
    doneTogether: 'Zusammen gemacht',
    shareHint: 'Jeder bekommt einen Teil der Sterne – aufgerundet.',
    starsFor: (stars: string, name: string) => `${stars} Sterne für ${name}`,
    eachGets: (n: number) => `Jeder bekommt ${n} ${n === 1 ? 'Stern' : 'Sterne'}`,
    done: 'Fertig',
    whoDidIt: 'Wer hat’s gemacht?',
    doneTogetherMore: 'Zusammen gemacht …',
    faster: (name: string) => `${name} war schneller`,
    forAllFirst: 'für alle · wer zuerst kommt',
    sharedFirst: 'geteilt · wer zuerst kommt',
    flex: { week: 'diese Woche', weekend: 'dieses Wochenende', month: 'diesen Monat' },
    togetherWith: (names: string) => `zusammen mit ${names}`,
    togetherAsk: 'Zusammen gemacht?',
    hide: 'Ausblenden',
    showDone: 'Erledigte ansehen',
    doneBy: (name: string) => `Erledigt von ${name}`,
    progress: (name: string, done: string, total: string) =>
      `${name}: ${done} von ${total} erledigt`,
    starsForGoal: 'Sterne fürs Familienziel',
    noTodos: 'Keine Aufgaben – schön!',
    todayOf: (name: string) => `Heute von ${name}`,
    doneOf: (done: string, total: string) => `${done} von ${total} erledigt`,
    parentDone: 'Aufgaben geschafft – danke fürs Familienziel!',
    todos: 'Einmal-Aufgaben',
    together: 'gemeinsam',
    oneOff: 'einmalig',
    today: 'heute',
    allDone: 'Alles geschafft für heute!',
    nothingNow: 'Gerade nichts – später geht es weiter.',
    forAll: 'Für alle',
    firstComes: 'wer zuerst kommt',
    // tasks page
    tapHint: 'Tippen hakt ab. Aufgaben mit Freigabe landen danach bei Mama oder Papa.',
    task: 'Aufgabe',
    todayHeading: 'Heute',
    todosAndAll: 'Einmal-Aufgaben und für alle',
    newTask: 'Neue Aufgabe',
    nameDone: (name: string) => `${name}: geschafft!`,
    routineDone: (routine: string) => `${routine} geschafft!`,
    wellDone: 'Super gemacht',
    // person page
    wishList: 'Wunschzettel',
    progressTo: (goal: string) => `Fortschritt zu ${goal}`,
    balanceOf: (balance: string, cost: string, unit: string) => `${balance} von ${cost} ${unit}`,
    nextGoal: 'Nächstes Ziel',
    reached: 'Geschafft – einlösen!',
    left: (n: string) => `noch ${n}`,
    myDay: 'Mein Tag',
    noEventsToday: 'Heute keine Termine.',
    myTodos: 'Meine Aufgaben',
    myTasks: 'Meine Aufgaben',
    kitaTimes: 'Kita-Zeiten',
    timetable: 'Stundenplan',
    myWeek: 'Meine Woche',
    rewards: 'Belohnungen',
    last7: 'Die letzten 7 Tage',
    streak: (n: string) => `${n} Tage am Stück:`,
    loading: 'Lädt …',
    // edit at the card (1.28)
    editLabel: (title: string) => `${title} bearbeiten`,
    editJob: 'Aufgabe bearbeiten',
    edit: 'Bearbeiten',
    remove: 'Löschen',
    removeAsk: 'Wirklich löschen? Das lässt sich nicht rückgängig machen.',
    removeYes: 'Ja, löschen',
    keep: 'Behalten',
    holdTip: 'Tipp: Aufgabe lange drücken (am PC: Stift) – so bearbeitest oder löschst du sie.',
    gotIt: 'Verstanden',
  },
  en: {
    stars: 'stars',
    points: 'points',
    dayparts: {
      morning: 'Mornings',
      afternoon: 'Afternoons',
      evening: 'Evenings',
      anytime: 'Any time today',
    },
    now: ' · now',
    whoHelped: 'Who joined in?',
    doneTogether: 'Done together',
    shareHint: 'Everyone gets a share of the stars – rounded up.',
    starsFor: (stars: string, name: string) => `${stars} stars for ${name}`,
    eachGets: (n: number) => `Everyone gets ${n} ${n === 1 ? 'star' : 'stars'}`,
    done: 'Done',
    whoDidIt: 'Who did it?',
    doneTogetherMore: 'Done together …',
    faster: (name: string) => `${name} was quicker`,
    forAllFirst: 'for everyone · first come, first served',
    sharedFirst: 'shared · first come, first served',
    flex: { week: 'this week', weekend: 'this weekend', month: 'this month' },
    togetherWith: (names: string) => `together with ${names}`,
    togetherAsk: 'Done together?',
    hide: 'Hide',
    showDone: 'Show done tasks',
    doneBy: (name: string) => `Done by ${name}`,
    progress: (name: string, done: string, total: string) => `${name}: ${done} of ${total} done`,
    starsForGoal: 'Stars for the family goal',
    noTodos: 'No tasks – lovely!',
    todayOf: (name: string) => `${name}’s day`,
    doneOf: (done: string, total: string) => `${done} of ${total} done`,
    parentDone: 'Tasks done – thanks for helping the family goal!',
    todos: 'One-off tasks',
    together: 'together',
    oneOff: 'one-off',
    today: 'today',
    allDone: 'All done for today!',
    nothingNow: 'Nothing right now – more later.',
    forAll: 'For everyone',
    firstComes: 'first come, first served',
    tapHint: 'Tap to tick off. Tasks that need approval then go to Mum or Dad.',
    task: 'Task',
    todayHeading: 'Today',
    todosAndAll: 'One-off tasks and for everyone',
    newTask: 'New task',
    nameDone: (name: string) => `${name}: done!`,
    routineDone: (routine: string) => `${routine} done!`,
    wellDone: 'Well done',
    wishList: 'Wish list',
    progressTo: (goal: string) => `Progress towards ${goal}`,
    balanceOf: (balance: string, cost: string, unit: string) => `${balance} of ${cost} ${unit}`,
    nextGoal: 'Next goal',
    reached: 'Done it – redeem now!',
    left: (n: string) => `${n} to go`,
    myDay: 'My day',
    noEventsToday: 'No events today.',
    myTodos: 'My tasks',
    myTasks: 'My tasks',
    kitaTimes: 'Nursery times',
    timetable: 'Timetable',
    myWeek: 'My week',
    rewards: 'Rewards',
    last7: 'The last 7 days',
    streak: (n: string) => `${n} days in a row:`,
    loading: 'Loading …',
    editLabel: (title: string) => `Edit ${title}`,
    editJob: 'Edit task',
    edit: 'Edit',
    remove: 'Delete',
    removeAsk: 'Really delete? This can’t be undone.',
    removeYes: 'Yes, delete',
    keep: 'Keep',
    holdTip: 'Tip: press and hold a task (on a computer: the pencil) to edit or delete it.',
    gotIt: 'Got it',
  },
} as const;

export const tasksText = TASKS_TEXT[locale];
